// @flow
import React, { useCallback, useMemo } from "react";
import Icons from "@hs/icons";
import loc from "i18next";
import styles from "./FolderTree.module.css";
import { getLocalizedText, isValidItemUri } from "data/utils";
import { getClassNames } from "data/utils";
import { openItemActionPanel } from "components/ItemActionPanel";
import { isHandheld } from "data/bowser";
import RowContentWithStickyToggle from "./RowContent";

const Row = ({
    index,
    isScrolling,
    isVisible,
    style,
    hasMore,
    tree,
    onLoadMore,
    activeTreeNodeId,
    onDoubleClick,
    onClick,
    onExpand,
    filter,
    viewName,
}: Props) => {
    const treeItem = tree[index];

    /* istanbul ignore next */
    const localizedWebDavName = useMemo(
        () =>
            isValidItemUri(treeItem?.itemUri)
                ? treeItem?.webDavName
                : getLocalizedText(treeItem?.webDavName),
        [treeItem]
    );

    const handleDoubleClick = useCallback(
        (e) => onDoubleClick(treeItem, e),
        [treeItem, onDoubleClick]
    );
    const handleClick = useCallback(
        (e) => onClick(treeItem, e),
        [treeItem, onClick]
    );

    const handleContextClick = useCallback(
        (e) => {
            e.preventDefault();
            openItemActionPanel({
                itemUri: treeItem?.itemUri,
                title: localizedWebDavName,
                contextProps: { itemUri: treeItem?.itemUri },
                targetView: viewName,
            });
            onClick(treeItem, e);
        },
        [treeItem, localizedWebDavName, onClick, viewName]
    );

    if (hasMore && index === tree.length) {
        return (
            <div
                className={[styles.listGroupItem, styles.hasMore].join(" ")}
                key="TREE$MORE"
                onClick={onLoadMore}
                style={style}
            >
                {loc.t("folder:treefilter.loadmore")}
                {"  "}
                <Icons.Library name="forward-step" />
            </div>
        );
    }

    return (
        <div
            className={getClassNames(
                styles.listGroupItem,
                activeTreeNodeId === treeItem.id
                    ? styles.listGroupItemActive
                    : ""
            )}
            key={treeItem.id}
            style={style}
            title={treeItem.bc || ""}
            onDoubleClick={handleDoubleClick}
            onClick={handleClick}
            onContextMenu={handleContextClick}
            data-test={`folderTreeRow-${localizedWebDavName.replace(" ", "")}`}
        >
            <RowContentWithStickyToggle
                index={index}
                isScrolling={isScrolling}
                isVisible={isVisible}
                hasMore={hasMore}
                tree={tree}
                onLoadMore={onLoadMore}
                activeTreeNodeId={activeTreeNodeId}
                onDoubleClick={onDoubleClick}
                onExpand={onExpand}
                filter={filter}
                localizedWebDavName={localizedWebDavName}
                treeItem={treeItem}
                onMoreClick={handleContextClick}
                isActive={activeTreeNodeId === treeItem.id}
                isHovered={!isHandheld()}
            />
        </div>
    );
};

export default Row;
